import * as Sentry from "@sentry/react";
import * as React from "react";

import { InvalidLayoutDefinitionError } from "@/shared/domain/layouts/LayoutDefinition";
import { UnsupportedLayoutSchemaVersion } from "@/shared/domain/layouts/schemas";
import { LoggerService } from "@/shared/services/LoggerService";
import { actions, useVizDispatch } from "@/shared/state/visualization";
import { cyclingColorScale } from "@/shared/state/visualization/CyclingColorScale";

import { layoutToVizConfig } from "../../layouts/LayoutToVizConfig";
import { useWorkspaceLoaderData } from "../../loader";
import { EphemeralWorkspaceState } from "../../WorkspaceCtx/EphemeralWorkspaceState";
import { selectAllWorkspaceTopicTrees } from "../../WorkspaceCtx/hooks";
import { HydrationError } from "../HydrationError";

/**
 * Loads the active layout into the visualizer when it changes.
 */
export function useLayoutHydration(workspaceContext: EphemeralWorkspaceState) {
  const {
    activeLayout,
    isDataLoading,
    setDataMatchingErrors,
    setWorkspaceSnapshot,
    setIsHydrating,
    skipLayoutRehydration,
  } = workspaceContext;

  const dispatchVizAction = useVizDispatch();
  const workspaceResult = useWorkspaceLoaderData();
  const topicTrees = selectAllWorkspaceTopicTrees(workspaceContext);
  const [error, setError] = React.useState<HydrationError | null>(null);

  // Track if this is the first layout load
  const isInitialLoad = React.useRef(true);

  // Track the previous layout ID to detect actual layout changes
  const prevLayoutIdRef = React.useRef<string | null>(null);

  const loadActiveLayout = React.useCallback(
    function loadLayout(): void {
      LoggerService.log("Loading active layout", activeLayout);

      // Reset the color scale so that panels get new colors when the layout changes
      cyclingColorScale.reset();

      const { panels, layout, errors } = layoutToVizConfig(activeLayout, {
        topicTrees,
      });

      dispatchVizAction(actions.replacePanelsAndLayout(panels, layout));
      setDataMatchingErrors(errors);
      setWorkspaceSnapshot({ panels, layout });
      setIsHydrating(false);
    },
    [
      activeLayout,
      dispatchVizAction,
      setDataMatchingErrors,
      setIsHydrating,
      setWorkspaceSnapshot,
      topicTrees,
    ],
  );

  const handleLayoutError = React.useCallback((e: unknown) => {
    if (e instanceof InvalidLayoutDefinitionError) {
      setError(
        new HydrationError(
          "invalid-layout-definition",
          "Failed to load workspace from layout because it is malformed. Try a different layout.",
          { cause: e },
        ),
      );
    } else if (e instanceof UnsupportedLayoutSchemaVersion) {
      setError(
        new HydrationError(
          "unsupported-layout-schema-version",
          "Failed to load workspace from layout because the layout schema version is unsupported. Try a different layout.",
          { cause: e },
        ),
      );
    } else {
      setError(
        new HydrationError(
          "change-layout-error",
          "Failed to load workspace from layout. Try again.",
          { cause: e },
        ),
      );
    }
  }, []);

  React.useEffect(() => {
    if (isDataLoading) {
      return;
    }

    // Handle initial load
    if (isInitialLoad.current) {
      isInitialLoad.current = false;

      // If no layout to resolve, just finish hydration
      if (workspaceResult.configSource?.type !== "layout") {
        // Set ref to prevent future runs from thinking the layout changed
        prevLayoutIdRef.current = activeLayout.layout_id;
        setIsHydrating(false);
        return;
      }
    }

    // Skip reload if flag is set
    if (skipLayoutRehydration) {
      return;
    }

    // Only load the layout when it has actually changed (not just when
    // topicTrees changed). A workspace with no topic trees hydrates all the
    // same: `isDataLoading` above already covers every fetch, so empty trees
    // here mean this workspace's data is absent, not late. Its rules bind to
    // nothing and come back as `errors` for the panels to report — and the
    // pass ends with `setIsHydrating(false)`, which is what lifts the panel
    // board's loading indicator and opens the AI workspace action bus.
    const layoutChanged = prevLayoutIdRef.current !== activeLayout.layout_id;

    if (!layoutChanged && prevLayoutIdRef.current !== null) {
      // Layout hasn't changed and this isn't the first load - skip reload
      return;
    }

    // Recorded before the pass runs, so topic trees arriving afterwards (the
    // user adds a file) take the skip above rather than a second wholesale
    // panel swap — one that would destroy whatever the AI put on the board
    // after the gate opened. Same as for a workspace that hydrated with data.
    prevLayoutIdRef.current = activeLayout.layout_id;

    try {
      Sentry.startSpan(
        {
          name: "loadActiveLayout",
          op: "ui.task",
          attributes: {
            active_layout_id: activeLayout.layout_id,
            active_layout_name: activeLayout.name,
          },
        },
        loadActiveLayout,
      );
    } catch (e) {
      handleLayoutError(e);
      // A malformed, unsupported, or otherwise unloadable layout becomes a
      // notification here rather than propagating, so nothing downstream will
      // clear the flag. Left set, it strands the panel board on its loading
      // indicator and the workspace action bus on refusing actions for the
      // rest of the session.
      setIsHydrating(false);
    }
  }, [
    activeLayout,
    isDataLoading,
    loadActiveLayout,
    setIsHydrating,
    skipLayoutRehydration,
    workspaceResult.configSource?.type,
    handleLayoutError,
  ]);

  return { error };
}
